import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { MessageAttachment } from './types';

const MAX_ATTACHMENTS = 3;

interface MessageAttachmentContextProps {
  attachments: MessageAttachment[];
  addAttachment: (attachment: MessageAttachment) => void;
  removeAttachment: (position: number) => void;
  reset: () => void;
  attachmentLimitExceeded: boolean;
  setAttachmentLimitExceeded: (value: boolean) => void;
}

const MessageAttachmentContext = createContext<MessageAttachmentContextProps>({
  attachments: [],
  addAttachment: () => {},
  removeAttachment: () => {},
  reset: () => {},
  attachmentLimitExceeded: false,
  setAttachmentLimitExceeded: () => {},
});

export const useMessageAttachment = () => useContext(MessageAttachmentContext);

const MessageAttachmentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [attachmentLimitExceeded, setAttachmentLimitExceeded] = useState(false);

  const addAttachment = useCallback((attachment: MessageAttachment) => {
    setAttachments((a) => [...a, attachment]);
  }, []);

  // Might introduce id based removal on or after ticket DEV-16063
  const removeAttachment = useCallback((position: number) => {
    setAttachments((a) => a.filter((_, i) => i !== position));
  }, []);

  const reset = useCallback(() => {
    setAttachments([]);
    setAttachmentLimitExceeded(false);
  }, []);

  useEffect(() => {
    if (attachments.length < MAX_ATTACHMENTS && attachmentLimitExceeded) {
      setAttachmentLimitExceeded(false);
    }
  }, [attachments.length, attachmentLimitExceeded]);

  return (
    <MessageAttachmentContext.Provider
      value={{
        attachments,
        addAttachment,
        removeAttachment,
        reset,
        attachmentLimitExceeded,
        setAttachmentLimitExceeded,
      }}
    >
      {children}
    </MessageAttachmentContext.Provider>
  );
};

export default MessageAttachmentProvider;
